<script lang="ts">
	import type { Layout } from '$models/Layout/Layout';
	import type { NonSubscribableProduct } from '$models/NonSubscribableProduct/NonSubscribableProduct';
	import type { OrderConfigurationOrder } from '$models/OrderConfigurator/OrderConfigurator';
	import type { OrderConfiguratorConvertor } from '$models/OrderConfigurator/OrderConfiguratorConvertor';
	import type { Product } from '$models/Product/Product';
	import { page } from '$app/state';
	import QuantityValidationMessage from '$components/Quantity/QuantityValidationMessage.svelte';
	import VatLabel from '$components/Shop/VatLabel.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { getProductIntensityDots, PRODUCT_INTENSITY_MAX } from '$lib/product/intensity';
	import { getShopPresentation } from '$lib/shop/presentation.svelte';
	import { getConfiguratorState } from '$lib/state/configurator.svelte';
	import { isRegularProduct } from '$lib/type-helpers';
	import { formatCurrency, formatNumericString, validateQuantity } from '$lib/utils';

	interface Props {
		configurator: OrderConfiguratorConvertor;
		onSubmit: (order: OrderConfigurationOrder) => void;
	}

	let { configurator, onSubmit }: Props = $props();
	const uid = $props.id();

	const configuratorState = getConfiguratorState();
	const presentation = getShopPresentation();
	const site = getSiteContext();
	const layout = page.data.page.layout as Layout;
	const translations = layout.translations;

	let selectedCategory = $derived(configuratorState.selectedCategory());
	let selectedQuantity = $derived(configuratorState.selectedQuantity());
	let quantityMultiplier = $derived(configuratorState.quantityMultiplier());
	let selectedDisplayQuantity = $derived(selectedQuantity * quantityMultiplier);
	let quantityValidation = $derived(
		validateQuantity(configuratorState.quantities(), selectedQuantity)
	);
	let nextValidDisplayQuantity = $derived(
		quantityValidation.nextValid ? quantityValidation.nextValid * quantityMultiplier : null
	);
	let selectedFrequencyType: 'recurring' | 'onetime' = $state(
		configuratorState.selectedFrequency() > 0 ? 'recurring' : 'onetime'
	);
	let selectedConfigurationCategory = $derived.by(() => {
		if (!selectedCategory) {
			return;
		}

		return configurator.configuration.categories.find(
			({ productCategory }) => productCategory.id === selectedCategory.id
		);
	});
	let productSelectionSubtitle = $derived(
		selectedConfigurationCategory?.translations.productSelectionSubtitle
	);
	let userSelectedFrequency: number = $state(configuratorState.selectedFrequency() || 1);
	let canAddToCart = $derived(
		configuratorState.isValid() &&
			!configuratorState.isFetchingPrices() &&
			configuratorState.canSubmitPricing()
	);
	let validationBounceKey = $state(0);
	let prices = $derived.by(() => {
		const configuratorPrices = configuratorState.displayPrices(false, presentation.vat);
		const configuratorFromPrices = configuratorState.displayPrices(true, presentation.vat);
		return {
			recurring: {
				price: configuratorPrices.recurring,
				fromPrice: configuratorFromPrices.recurring,
				discount: configuratorPrices.recurringDiscount,
				fromDiscount: configuratorFromPrices.recurringDiscount
			},
			onetime: {
				price: configuratorPrices.onetime,
				fromPrice: configuratorFromPrices.onetime,
				discount: configuratorPrices.onetimeDiscount,
				fromDiscount: configuratorFromPrices.onetimeDiscount
			}
		};
	});

	const GRAMS_OF_BEANS_PER_CUP = 8;

	let isSelectionValid = $derived(configuratorState.isValidQuantity());
	let hasMadeSelection = $derived(selectedQuantity > 0);
	let isBeanCategory = $derived(selectedCategory?.icon === 'bean');
	let isCupCategory = $derived(selectedCategory?.icon === 'cup');
	let rawProductWeight = $derived(selectedCategory?.rawProductWeight ?? 0);

	let effectiveDisplayQuantity = $derived(
		hasMadeSelection
			? selectedDisplayQuantity
			: (configuratorState.fromPriceQuantity() ?? 0) * quantityMultiplier
	);
	let totalCups = $derived(
		isBeanCategory
			? Math.floor((effectiveDisplayQuantity * rawProductWeight * 1000) / GRAMS_OF_BEANS_PER_CUP)
			: effectiveDisplayQuantity
	);
	let cupsPerDay = $derived(
		totalCups > 0 ? Math.round(totalCups / (configuratorState.selectedRecurringFrequency() * 7)) : 0
	);
	let showDynamicPricing = $derived((isCupCategory || isBeanCategory) && totalCups > 0);

	function netPriceFor(type: 'recurring' | 'onetime'): number | null {
		if (isSelectionValid) {
			if (!configuratorState.isPricesCurrent()) {
				return null;
			}
			const price = prices[type].price;
			return price === null ? null : price - prices[type].discount;
		}
		if (!hasMadeSelection && configuratorState.hasFromPrices()) {
			if (!configuratorState.isFromPricesCurrent()) {
				return null;
			}
			const price = prices[type].fromPrice;
			return price === null ? null : price - prices[type].fromDiscount;
		}
		return null;
	}

	let heldPricePerCup = $state<number | null>(null);
	let heldPricePerKg = $state<number | null>(null);
	let heldOnetimePricePerCup = $state<number | null>(null);
	let heldPricesCategoryId: string | undefined;
	let heldPricesVat = $state(presentation.vat);

	$effect(() => {
		if (selectedCategory?.id !== heldPricesCategoryId || presentation.vat !== heldPricesVat) {
			heldPricesCategoryId = selectedCategory?.id;
			heldPricesVat = presentation.vat;
			heldPricePerCup = null;
			heldPricePerKg = null;
			heldOnetimePricePerCup = null;
		}

		if (totalCups <= 0 || !presentation.ready) {
			return;
		}

		const recurringNetPrice = netPriceFor('recurring');
		if (recurringNetPrice !== null) {
			heldPricePerCup = recurringNetPrice / totalCups;
			const totalKg = effectiveDisplayQuantity * rawProductWeight;
			heldPricePerKg = totalKg > 0 ? recurringNetPrice / totalKg : 0;
		}

		const onetimeNetPrice = netPriceFor('onetime');
		if (onetimeNetPrice !== null) {
			heldOnetimePricePerCup = onetimeNetPrice / totalCups;
		}
	});

	$effect(() => {
		if (selectedFrequencyType === 'onetime') {
			configuratorState.setFrequency(0);
		} else {
			configuratorState.setFrequency(userSelectedFrequency);
			configuratorState.setRecurringFrequency(userSelectedFrequency);
		}
	});

	function formatSackWeight(weightInKg: number): string {
		return weightInKg < 1 ? `${Math.round(weightInKg * 1000)} gram` : `${weightInKg} kilo`;
	}

	let categoryGroups = $derived(configuratorState.categoryGroups());
	let selectedCategoryGroup = $derived(
		categoryGroups.find((group) => group.icon === selectedCategory?.icon)
	);

	function handleFrequencyChange(e) {
		const newFrequency = parseInt(e.target.value);
		userSelectedFrequency = newFrequency;
		configuratorState.setRecurringFrequency(newFrequency);
	}

	function handleQuantityInputChange(product: Product | NonSubscribableProduct, newValue: string) {
		const value = parseInt(newValue);

		if (Number.isNaN(value)) {
			configuratorState.setProductQuantity(product, 0);
			return;
		}

		if (isRegularProduct(product) && value > 0) {
			configuratorState.setProductQuantity(product, value * (product.quantityMultiplier ?? 1));
			return;
		}

		configuratorState.setProductQuantity(product, value);
	}

	$effect(() => {
		if (configuratorState.isValidQuantity()) {
			validationBounceKey = 0;
		}
	});

	function triggerValidationBounce() {
		validationBounceKey++;
	}

	function handleSubmit() {
		if (!canAddToCart) {
			triggerValidationBounce();
			return;
		}

		onSubmit(configuratorState.order());
	}
</script>

{#snippet pricesSnippet(type: 'onetime' | 'recurring')}
	{@const price = configuratorState.isValid() ? prices[type].price : prices[type].fromPrice}
	{@const discount = configuratorState.isValid()
		? prices[type].discount
		: prices[type].fromDiscount}
	<div
		class={[
			'convertor-configurator-frequency-prices',
			configuratorState.isFetchingPrices() && 'is-loading'
		]}
	>
		{#if !configuratorState.isValid() && !configuratorState.isFetchingPrices() && configuratorState.canSubmitPricing() && configuratorState.hasFromPrices()}
			<span class="{type}-regular-price convertor-configurator-frequency-price-from"
				>{translations.fromPriceLabel}</span
			>
		{/if}

		{#if configuratorState.isFetchingPrices()}
			<span class="convertor-configurator-frequency-price-loading">
				<span class="convertor-configurator-frequency-price-loader convertor-configurator-loader"
				></span>
			</span>
		{:else if price !== null && discount > 0}
			<span class="{type}-regular-price convertor-configurator-frequency-price-old"
				>{formatCurrency(price, site.locale.code)}</span
			>
			<span class="{type}-discounted-price convertor-configurator-frequency-price"
				>{formatCurrency(price - discount)}</span
			>
		{:else if price !== null}
			<span class="{type}-discounted-price convertor-configurator-frequency-price">
				{formatCurrency(price, site.locale.code)}
			</span>
		{/if}
	</div>
	<VatLabel />
{/snippet}

<div class="convertor-configurator">
	<div class="convertor-configurator-top">
		{#if categoryGroups.length > 1}
			<div class="convertor-configurator-toggles">
				{#each categoryGroups as group (group.icon)}
					<button
						type="button"
						class={[
							'convertor-configurator-toggle',
							selectedCategory?.icon === group.icon && 'is-active'
						]}
						onclick={() => configuratorState.selectGroup(group.icon)}>{group.label}</button
					>
				{/each}
			</div>
		{/if}
	</div>

	{#if configurator.productSelectionTitle || productSelectionSubtitle}
		<div class="convertor-configurator-header">
			<h3 class="convertor-configurator-title">{configurator.productSelectionTitle}</h3>
			{#if productSelectionSubtitle}
				<p class="convertor-configurator-subtitle">{productSelectionSubtitle}</p>
			{/if}
		</div>
	{/if}

	<div class="convertor-configurator-body">
		<div class="convertor-configurator-column">
			{#if selectedCategoryGroup && selectedCategoryGroup.categories.length > 1}
				<div class="convertor-configurator-weight-filters">
					{#each selectedCategoryGroup.categories as category (category.id)}
						<button
							type="button"
							class={[
								'convertor-configurator-weight-filter',
								selectedCategory?.id === category.id && 'is-active'
							]}
							onclick={() => configuratorState.setCategory(category)}
						>
							{formatSackWeight(category.rawProductWeight)}
						</button>
					{/each}
				</div>
			{/if}

			<div class="convertor-configurator-products">
				{#each configuratorState.products() as { product, quantity } (product.id)}
					{@const quantityMultiplier = product.quantityMultiplier ?? 1}
					{@const displayQuantity = quantity * quantityMultiplier}
					<div class="convertor-configurator-product">
						<!-- TODO: Show larger images when there are 3 or less products. Is this a future-proof solution? -->
						<figure
							class={[
								'convertor-configurator-product-image',
								configuratorState.products().length <= 3 &&
									'convertor-configurator-product-image-large'
							]}
						>
							{#if product.image}
								<img
									src={product.image.sizes.thumbnail}
									srcset={getWordPressImageSrcSet(product.image)}
									sizes="36px"
									alt={product.name}
									width={product.image.width}
									height={product.image.height}
									loading="lazy"
									decoding="async"
								/>
							{/if}
						</figure>
						<div class="convertor-configurator-product-info">
							<div class="convertor-configurator-product-body">
								<div class="convertor-configurator-product-title">
									{product.name}
								</div>
								<div class="convertor-configurator-product-input">
									{#if quantity > 0}
										<button
											class="convertor-configurator-product-quantity-button convertor-configurator-product-quantity-button--minus"
											onclick={() => configuratorState.decrementProductQuantity(product)}
										>
											<span class="screenreader">-</span>
										</button>

										<input
											class="convertor-configurator-product-quantity"
											type="text"
											value={displayQuantity}
											onchange={(e) => handleQuantityInputChange(product, e.currentTarget.value)}
										/>
									{/if}

									<button
										class="convertor-configurator-product-quantity-button convertor-configurator-product-quantity-button--plus"
										onclick={() => configuratorState.incrementProductQuantity(product)}
									>
										<span class="screenreader">+</span>
									</button>
								</div>
							</div>
							{#if product.intensity}
								<div class="convertor-configurator-product-intensity">
									<div class="convertor-configurator-product-intensity-value">
										{#each getProductIntensityDots(product.intensity) as dot}
											<div
												class={[
													'convertor-configurator-product-intensity-value-item',
													dot.isActive && 'active',
													dot.isHalf && 'is-half'
												]}
											></div>
										{/each}
									</div>
									<div class="convertor-configurator-product-intensity-label">
										{product.intensity}/{PRODUCT_INTENSITY_MAX}
									</div>
								</div>
							{/if}
						</div>
					</div>
				{/each}
			</div>

			<div class="convertor-configurator-totals">
				{#if configurator.productSelectionTotalQuantityText}
					<div class="convertor-configurator-totals-label">
						{configurator.productSelectionTotalQuantityText}
					</div>
				{:else}
					<div class="convertor-configurator-totals-label">Totaal</div>
				{/if}

				<div class="convertor-configurator-totals-quantity">
					{selectedDisplayQuantity}

					{#if nextValidDisplayQuantity && !quantityValidation.isValid}
						<div class="convertor-configurator-totals-quantity-suffix">
							/{nextValidDisplayQuantity}
							{selectedCategory?.translations.unitNamePlural}
						</div>
					{:else if selectedDisplayQuantity === 1}
						{selectedCategory?.translations.unitNameSingle}
					{:else}
						{selectedCategory?.translations.unitNamePlural}
					{/if}
				</div>
			</div>
		</div>

		<div class="convertor-configurator-column">
			<div class="convertor-configurator-frequency">
				<!-- Recurring option -->
				<label
					class="convertor-configurator-frequency-item recurring"
					class:is-active={selectedFrequencyType === 'recurring'}
				>
					<input
						type="radio"
						name="{uid}-frequency_type"
						value="recurring"
						class="convertor-configurator-frequency-radio"
						checked={selectedFrequencyType === 'recurring'}
						bind:group={selectedFrequencyType}
					/>
					<div class="convertor-configurator-frequency-inner">
						<div class="convertor-configurator-frequency-header">
							<span class="convertor-configurator-frequency-label">
								{#if configurator.recurringOrderSelectLabelSingle && configurator.recurringOrderSelectLabelPlural}
									{formatNumericString(
										configurator.recurringOrderSelectLabelSingle,
										configurator.recurringOrderSelectLabelPlural,
										selectedDisplayQuantity,
										'%quantity%'
									)
										.replaceAll('%product%', selectedCategory?.translations.unitNameSingle ?? '')
										.replaceAll('%products%', selectedCategory?.translations.unitNamePlural ?? '')}
								{/if}
							</span>

							<select
								class="convertor-configurator-frequency-select"
								onchange={handleFrequencyChange}
							>
								{#each configuratorState.frequencies() as frequency}
									{#if frequency > 0}
										<option
											value={frequency}
											selected={configuratorState.selectedRecurringFrequency() == frequency}
										>
											{formatNumericString(
												translations.frequencySingle,
												translations.frequencyMultiple,
												frequency,
												'%quantity%'
											)}
										</option>
									{/if}
								{/each}
							</select>
						</div>

						{@render pricesSnippet('recurring')}
					</div>

					<div class="convertor-configurator-frequency-text rich-text">
						{#if showDynamicPricing}
							<ul class="convertor-configurator-frequency-text rich-text">
								{#if configurator.recurringOrderCupsPerDaySingleText && configurator.recurringOrderCupsPerDayPluralText}
									<li>
										{formatNumericString(
											configurator.recurringOrderCupsPerDaySingleText,
											configurator.recurringOrderCupsPerDayPluralText,
											cupsPerDay,
											'%quantity%'
										)}
									</li>
								{/if}
								{#if configurator.recurringOrderPricePerCupText && heldPricePerCup !== null && heldPricesVat === presentation.vat && presentation.ready}
									<li>
										{configurator.recurringOrderPricePerCupText.replaceAll(
											'%price%',
											formatCurrency(heldPricePerCup, site.locale.code)
										)}
										{#if isBeanCategory && configurator.recurringOrderPricePerKgText && heldPricePerKg !== null}
											{configurator.recurringOrderPricePerKgText.replaceAll(
												'%price%',
												formatCurrency(heldPricePerKg, site.locale.code)
											)}
										{/if}
										<VatLabel />
									</li>
								{/if}
							</ul>
						{/if}

						{#if configurator.recurringOrderText}
							<!-- Make sure WYSIWYG tags like ul, strong etc are preserved -->
							{@html configurator.recurringOrderText}
						{/if}
					</div>
				</label>

				<!-- Onetime option -->
				<label
					class="convertor-configurator-frequency-item onetime"
					class:is-active={selectedFrequencyType === 'onetime'}
				>
					<input
						type="radio"
						name="{uid}-frequency_type"
						value="onetime"
						class="convertor-configurator-frequency-radio"
						bind:group={selectedFrequencyType}
					/>
					<div class="convertor-configurator-frequency-inner">
						<div class="convertor-configurator-frequency-header">
							<span class="convertor-configurator-frequency-label">
								{translations.frequencyOnetime}
							</span>
						</div>

						{@render pricesSnippet('onetime')}
					</div>

					{#if showDynamicPricing}
						<ul class="convertor-configurator-frequency-text rich-text">
							{#if configurator.onetimeOrderPricePerCupText && heldOnetimePricePerCup !== null && heldPricesVat === presentation.vat && presentation.ready}
								<li>
									{configurator.onetimeOrderPricePerCupText.replaceAll(
										'%price%',
										formatCurrency(heldOnetimePricePerCup, site.locale.code)
									)}
									<VatLabel />
								</li>
							{/if}
							{#if isBeanCategory && configurator.onetimeOrderTotalCupsSingleText && configurator.onetimeOrderTotalCupsPluralText}
								<li>
									{formatNumericString(
										configurator.onetimeOrderTotalCupsSingleText,
										configurator.onetimeOrderTotalCupsPluralText,
										totalCups,
										'%quantity%'
									)}
								</li>
							{/if}
						</ul>
					{/if}

					{#if configurator.onetimeOrderText}
						<!-- Make sure WYSIWYG tags like ul, strong etc are preserved -->
						<div class="convertor-configurator-frequency-text rich-text">
							{@html configurator.onetimeOrderText}
						</div>
					{/if}
				</label>
			</div>

			<div class="convertor-configurator-footer">
				<button
					type="button"
					class={[
						'convertor-configurator-button button button--arrow',
						configuratorState.isFetchingPrices() && 'is-loading',
						!canAddToCart && 'is-disabled'
					]}
					aria-disabled={!canAddToCart}
					onclick={handleSubmit}
				>
					{#if configuratorState.isFetchingPrices()}
						<span class="convertor-configurator-button-loader convertor-configurator-loader"></span>
					{:else}
						{configurator.orderButtonText}
					{/if}

					{#if canAddToCart}
						{formatCurrency(
							selectedFrequencyType === 'recurring'
								? configuratorState.displayPrices(false, presentation.vat).recurringPayable!
								: configuratorState.displayPrices(false, presentation.vat).onetimePayable!
						)}
					{/if}
				</button>

				{#if !configuratorState.isValidQuantity()}
					{#key validationBounceKey}
						<div
							class={[
								'convertor-configurator-validation',
								validationBounceKey > 0 && 'is-bouncing'
							]}
						>
							<QuantityValidationMessage
								quantities={configuratorState.quantities()}
								quantity={configuratorState.selectedQuantity()}
								multiplier={configuratorState.quantityMultiplier()}
								{...selectedCategory?.translations}
							/>

							{#if translations.quantitySelector.message}
								<div class="convertor-configurator-validation-subtitle">
									{translations.quantitySelector.message}
								</div>
							{/if}
						</div>
					{/key}
				{/if}
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	.convertor-configurator {
		&-top {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;

			&:not(:last-child) {
				margin-bottom: 32px;
			}
		}

		&-toggles {
			display: flex;
			align-items: center;
			gap: 16px;
			padding: 6px;
			background-color: white;
			border-radius: 40px;
			box-shadow: 0px 4px 50px 0px #3232321a;
		}

		&-toggle {
			padding: 11px 18px;
			border-radius: 18px;
			color: #9e9990;
			font-family: var(--title-font-family);
			text-transform: var(--box-title-text-transform);
			font-size: 14px;
			font-weight: normal;
			line-height: 1;
			transition:
				background-color var(--transition),
				color var(--transition);

			&:hover {
				color: var(--color-brown);
			}

			&.is-active {
				color: var(--color-brown);
				background-color: var(--color-primary-light);
			}
		}

		&-weight-filters {
			display: flex;
			align-items: center;
			gap: 8px;

			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-weight-filter {
			padding: 8px 16px;
			border-radius: 20px;
			border: 1px solid var(--color-brown);
			background-color: transparent;
			color: var(--color-brown);
			font-size: 14px;
			transition:
				background-color var(--transition),
				color var(--transition),
				border-color var(--transition);

			&:hover {
				background-color: rgba(89, 46, 44, 0.08);
			}

			&.is-active {
				background-color: var(--color-brown);
				border-color: var(--color-brown);
				color: var(--color-white);
			}
		}

		&-header {
			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-title {
			font-size: 24px;
			line-height: 1;

			&:not(:last-child) {
				margin-bottom: 4px;
			}
		}

		&-subtitle {
			font-size: 18px;
		}

		&-body {
			display: grid;
			gap: 32px;
			min-height: 450px;
		}

		&-products {
			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-product {
			position: relative;
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 16px 0;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&:last-child::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&-image {
				width: 100%;
				max-width: 40px;
				aspect-ratio: 1;
				border-radius: 4px;
				overflow: hidden;
				flex-shrink: 0;
			}

			&-image-large {
				max-width: 80px;
			}

			&-info {
				display: flex;
				flex-direction: column;
				width: 100%;
				gap: 4px;
			}

			&-body {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: 8px;
			}

			&-title {
				font-family: var(--title-font-family);
				text-transform: var(--box-title-text-transform);
				line-height: 1;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&-input {
				display: flex;
				align-items: center;
				gap: 5px;
				flex-shrink: 0;
			}

			&-quantity {
				font-family: var(--title-font-family);
				text-transform: var(--box-title-text-transform);
				text-align: center;
				border: 0;
				border-top: 1px solid transparent;
				border-bottom: 1px solid transparent;
				padding: 8px 0;
				max-width: 28px;
				max-height: 24px;
				background-color: transparent;

				&:hover {
					cursor: text;
				}

				&:focus {
					outline: none;
					border-top: 1px solid transparent;
					border-bottom: 1px dotted var(--color-brown);
				}

				&-button {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 24px;
					height: 24px;
					color: var(--color-brown);
					border: 2px solid var(--color-brown);
					border-radius: 50%;
					transition: border-color var(--transition);

					&:before,
					&:after {
						content: '';
						position: absolute;
						width: 12px;
						height: 2px;
						background-color: var(--color-brown);
						transition: background-color var(--transition);
					}

					&:after {
						transform: rotate(90deg);
					}

					&--minus {
						&:after {
							display: none;
						}

						&:before {
							width: 10px;
						}
					}

					&--trash {
						&:after,
						&:before {
							display: none;
						}
					}

					&:hover {
						cursor: pointer;
						border-color: var(--color-primary-light);

						&:before,
						&:after {
							background-color: var(--color-primary-light);
						}
					}
				}
			}

			&-intensity {
				display: flex;
				align-items: center;
				gap: 4px;

				&-value {
					display: flex;
					align-items: center;
					gap: 3px;
				}

				&-value-item {
					width: 10px;
					height: 10px;
					border-radius: 50%;
					border: 1px solid var(--color-brown);

					&.is-half {
						background: linear-gradient(
							to right,
							var(--color-brown) 0%,
							var(--color-brown) 60%,
							transparent 60%,
							transparent 100%
						);
					}

					&.active {
						background-color: var(--color-brown);
					}
				}

				&-label {
					font-size: 12px;
					color: var(--color-brown);
				}
			}
		}

		&-totals {
			display: flex;
			align-items: center;
			justify-content: space-between;
			font-family: var(--title-font-family);
			text-transform: var(--box-title-text-transform);
			font-size: 20px;
			line-height: 1;

			&-quantity {
				display: flex;
				align-items: flex-end;
				gap: 4px;
			}

			&-quantity-suffix {
				font-size: 12px;
				font-family: var(--font-primary);
				text-transform: none;
				line-height: 1.25;
			}
		}

		&-frequency {
			display: grid;
			gap: 16px;

			&-item {
				display: flex;
				flex-direction: column;
				gap: 16px;
				padding: 16px;
				border-radius: 8px;
				border: 1px solid var(--color-brown);
				cursor: pointer;
				transition:
					background-color var(--transition),
					border-color var(--transition);

				&.is-active {
					background-color: var(--color-white);
					border-color: var(--color-white);
				}

				&:hover:not(.is-active),
				&:focus:not(.is-active) {
					background-color: rgba(255, 255, 255, 0.25);
				}
			}

			&-inner {
				display: flex;
				align-items: center;
				justify-content: space-between;
				flex-wrap: wrap;
				gap: 8px;
			}

			&-header {
				display: flex;
				align-items: center;
				gap: 6px;
				font-family: var(--title-font-family);
				text-transform: var(--box-title-text-transform);
				line-height: 1;
				color: var(--color-brown);
			}

			&-select {
				padding: 4px 24px 4px 8px;
				background-color: #ccf2db;
				border: 1px solid #ccf2db;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 13.41 8.12'%3E%3Cpath d='M13.41 1.41L6.7 8.12 0 1.41 1.41 0 6.7 5.29 12 0l1.41 1.41z' fill='%23592e2c'/%3E%3C/svg%3E%0A");
				background-size: 8px 5px;
				background-position: calc(100% - 8px) calc(50%);
				background-repeat: no-repeat;
				max-width: 140px;
				width: 100%;
				font-family: var(--title-font-family);
				text-transform: var(--box-title-text-transform);
				transition: border-color var(--transition);

				&:hover,
				&:focus {
					border-color: var(--color-green);
				}

				option {
					font-family: var(--font-primary);
					text-transform: none;
				}
			}

			&-radio {
				position: absolute;
				width: 1px;
				height: 1px;
				margin: -1px;
				padding: 0;
				clip: rect(1px, 1px, 1px, 1px);
				border: 0;
				word-wrap: normal;
				overflow: hidden;
				clip-path: inset(50%);

				& ~ .convertor-configurator-frequency-inner .convertor-configurator-frequency-label {
					display: inline-block;
					position: relative;
					padding-left: 28px;
					line-height: 22px;
					font-size: 18px;

					&::before,
					&::after {
						content: '';
						position: absolute;
						top: 1px;
						left: 0;
						border-radius: 50%;
					}

					&::before {
						width: 22px;
						height: 22px;
						transition:
							border-color var(--transition),
							box-shadow var(--transition);
						border: 2px solid var(--color-brown);
						background-color: transparent;
					}

					&::after {
						width: 12px;
						height: 12px;
						margin: 5px;
						transform: scale(0);
						transform-origin: center center;
						transition:
							transform var(--transition),
							opacity var(--transition);
						opacity: 0;
						background-color: var(--color-brown);
					}
				}

				&:hover {
					& ~ .convertor-configurator-frequency-inner .convertor-configurator-frequency-label {
						&::before {
							border-color: var(--color-brown);
						}
					}
				}

				&:disabled {
					& ~ .convertor-configurator-frequency-inner .convertor-configurator-frequency-label {
						opacity: 0.35;
					}
				}

				&:checked {
					& ~ .convertor-configurator-frequency-inner .convertor-configurator-frequency-label {
						&::after {
							transform: scale(1);
							opacity: 1;
						}
					}
				}
			}

			&-prices {
				display: flex;
				align-self: center;
				gap: 6px;
			}

			&-price {
				font-size: 18px;
				font-family: var(--title-font-family);
				line-height: 1;
				text-transform: var(--box-title-text-transform);
			}

			&-price-loading {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 16px;
			}

			&-price-old,
			&-price-from {
				font-size: 14px;
				line-height: 1.6;
			}

			&-price-old {
				text-decoration: line-through;
			}

			&-text {
				font-size: 14px;
				line-height: 1.6;
				color: var(--color-brown);

				:global(ul) {
					margin-left: 0;
					padding-left: 8px;
					margin-bottom: 0;
					list-style: none;
				}

				:global(li) {
					position: relative;
					padding-left: 12px;
					list-style: none;
					line-height: 1.6;
				}

				:global(li::before) {
					content: '';
					position: absolute;
					left: 0;
					top: 9px;
					width: 3px;
					height: 3px;
					border-radius: 50%;
					background-color: var(--color-brown);
				}
			}

			&:not(:last-child) {
				margin-bottom: 32px;
			}
		}

		&-footer {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			justify-content: center;
			gap: 16px;
		}

		&-button {
			display: flex;
			align-items: center;
			color: var(--button-color);
			background-color: var(--button-background);
			padding-left: 18px;
			padding-right: 18px;
			width: 100%;
			text-align: center;
			justify-content: center;

			&:after {
				border-right: 2px solid var(--button-color);
				border-bottom: 2px solid var(--button-color);
			}

			&:hover:not(.is-disabled):not(.is-loading),
			&:focus:not(.is-disabled):not(.is-loading) {
				cursor: pointer;
				color: var(--button-color-hover);
				background-color: var(--button-background-hover);

				&:after {
					border-right-color: var(--button-color-hover);
					border-bottom-color: var(--button-color-hover);
				}
			}

			&.is-disabled {
				cursor: default;
				background-color: #e0ddd9;
				color: #4a453f;
				opacity: 1;

				&:after {
					border-right-color: #4a453f;
					border-bottom-color: #4a453f;
				}
			}

			&.is-loading {
				cursor: default;
				opacity: 1;

				&:after {
					display: none;
				}
			}
		}

		&-loader {
			width: 16px;
			aspect-ratio: 1;
			border-radius: 50%;
			border: 3px solid rgba(89, 46, 44, 0.43);
			border-right-color: var(--color-brown);
			animation: loader 1s infinite linear;

			@keyframes loader {
				to {
					transform: rotate(1turn);
				}
			}
		}

		&-message {
			display: flex;
			align-items: center;
			gap: var(--spacing-2);
			font-size: 14px;
			line-height: var(--font-lineheight-0);
			padding: var(--spacing-3);
			background-color: var(--color-primary-light);
		}

		&-validation {
			text-align: center;
			font-family: var(--title-font-family);
			text-transform: var(--box-title-text-transform);
			font-size: 16px;
			line-height: 1.2;
			color: #4a453f;

			&.is-bouncing {
				animation: convertor-configurator-validation-bounce 1.2s ease-out;
			}

			@keyframes convertor-configurator-validation-bounce {
				0%,
				60%,
				100% {
					transform: scale(1);
				}

				15%,
				45% {
					transform: scale(1.05);
				}

				30% {
					transform: scale(1);
				}
			}
		}

		&-validation-subtitle {
			font-family: var(--font-primary);
			text-transform: none;
			font-size: 14px;
			line-height: 1.6;
		}

		@media (--xs) {
			&-frequency {
				&-prices {
					align-self: flex-end;
				}
			}
		}

		@media (--lg) {
			&-top {
				&:not(:last-child) {
					margin-bottom: 48px;
				}
			}

			&-body {
				grid-template-columns: minmax(0, 0.5fr) minmax(0, 0.5fr);
				gap: 64px;
				min-height: 480px;
			}

			&-title {
				font-size: 28px;
			}

			&-weight-filter {
				font-size: 16px;
			}

			&-product {
				&-image {
					max-width: 50px;
				}

				&-image-large {
					max-width: 80px;
				}

				&-title {
					font-size: 18px;
				}
			}

			&-frequency {
				&-price-loading {
					&,
					.convertor-configurator-loader {
						width: 18px;
					}
				}
			}

			&-footer {
				gap: 24px;
			}
		}

		@media (--xl) {
			&-top {
				&:not(:last-child) {
					margin-bottom: 60px;
				}
			}

			&-body {
				gap: 80px;
				min-height: 500px;
			}

			&-validation {
				font-size: 20px;
			}
		}
	}
</style>
